import { Dictionary, groupBy, isEmpty, map, size } from 'lodash';
import pluralize from 'pluralize';

import { SlideInferenceResults } from 'interfaces/calculateFeatures';
import { InputCategory, MissingInputsBySlideId } from '../calculateFeaturesInputs';
import { getSidesWithoutSomeModels, SlidesModels } from './utils';

export type SlideInputValidationType = 'missing_slide_selections' | 'missing_required_inputs';

export type SlideInputValidationSeverity = 'error' | 'warning';

export type SlideInputValidationSlide = {
  slideId: string;
  stainingType?: string;
  missingInputs?: InputCategory[];
};

export type SlideInputValidation = {
  type: SlideInputValidationType;
  severity: SlideInputValidationSeverity;
  title: string;
  description: string;
  slidesByStain: Record<string, SlideInputValidationSlide[]>;
  slideIds: string[];
};

export const getSlideInputValidations = (params: {
  slidesModels: SlidesModels[];
  modelsType: string[];
  missingInputsBySlideId?: MissingInputsBySlideId;
  slidesById: Dictionary<SlideInferenceResults>;
}): SlideInputValidation[] => {
  const { slidesModels, modelsType, missingInputsBySlideId, slidesById } = params;
  const validations: SlideInputValidation[] = [];

  const slidesWithoutSomeModels = getSidesWithoutSomeModels(modelsType, slidesModels);

  if (!isEmpty(slidesWithoutSomeModels)) {
    const slidesByStain = groupBy(slidesWithoutSomeModels, (slide) => slide.stainingType || 'Unknown');

    validations.push({
      type: 'missing_slide_selections',
      severity: 'error',
      title: 'Selections missing',
      description: 'Some slides have no selections. Add inference models or assignments for those slides.',
      slidesByStain,
      slideIds: map(slidesWithoutSomeModels, 'slideId'),
    });
  }

  if (!isEmpty(missingInputsBySlideId)) {
    const missingInputsEntries = map(missingInputsBySlideId, (missingInputs, slideId) => ({
      slideId,
      stainingType: slidesById[slideId]?.stainingType,
      missingInputs,
    }));

    const slidesByStain = groupBy(missingInputsEntries, (entry) => entry.stainingType || 'Unknown');

    validations.push({
      type: 'missing_required_inputs',
      severity: 'warning',
      title: 'Missing inputs',
      description: `Some slides are missing required inputs from previous results. Add inputs in the sections above or remove those slides (${size(
        missingInputsEntries
      )} ${pluralize('slide', size(missingInputsEntries))}).`,
      slidesByStain,
      slideIds: map(missingInputsEntries, 'slideId'),
    });
  }

  return validations;
};
